<template>
  <div>
    <div class="page-header">
      <h1 class="page-title">Check-in</h1>
      <div class="pending-counter">
        <span class="counter-num">{{ pendingCount }}</span>
        <span class="counter-label">pendente{{ pendingCount === 1 ? '' : 's' }}</span>
      </div>
    </div>

    <div class="search-box">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" class="search-icon"><path fill="currentColor" d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0c.41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5S14 7.01 14 9.5S11.99 14 9.5 14z"/></svg>
      <input class="input search-input" type="text" v-model="searchQuery" placeholder="Buscar acampante..." />
      <button v-if="searchQuery" class="clear-btn" @click="searchQuery = ''" aria-label="Limpar busca">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12z"/></svg>
      </button>
    </div>

    <SkeletonLoader v-if="store.loading && store.acampantes.length === 0" variant="table-row" :count="6" />

    <template v-else>
      <div class="checkin-list" v-if="filteredPending.length > 0">
        <div class="checkin-item card" v-for="item in filteredPending" :key="item.id">
          <div class="item-main">
            <div class="item-name-row">
              <span class="item-name">{{ item.name }}</span>
              <EquipeChip :equipe="item.equipe" />
            </div>
            <span class="item-contato" v-if="item.contato">{{ item.contato }}</span>
          </div>
          <button class="btn-checkin" @click="openCheckin(item)">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19L21 7l-1.41-1.41L9 16.17z"/></svg>
            <span>Check-in</span>
          </button>
        </div>
      </div>

      <EmptyState
        v-else-if="!searchQuery && pendingCount === 0"
        title="Todos confirmados! 🎉"
        description="Não há acampantes pendentes de check-in."
      />

      <EmptyState
        v-else-if="searchQuery && filteredAlreadyConfirmed.length === 0"
        title="Nenhum acampante encontrado"
        description="Verifique se o nome está correto ou se está inscrito."
      />

      <!-- Bloco reduzido: quem o operador buscou e já confirmou (só aviso, sem clique) -->
      <div v-if="searchQuery && filteredAlreadyConfirmed.length > 0" class="already-section">
        <div class="already-header">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="m10.6 16.6l7.05-7.05l-1.4-1.4l-5.65 5.65l-2.85-2.85l-1.4 1.4l4.25 4.25ZM12 22q-2.075 0-3.9-.788t-3.175-2.137q-1.35-1.35-2.137-3.175T2 12q0-2.075.788-3.9t2.137-3.175q1.35-1.35 3.175-2.137T12 2q2.075 0 3.9.788t3.175 2.137q1.35 1.35 2.138 3.175T22 12q0 2.075-.788 3.9t-2.137 3.175q-1.35 1.35-3.175 2.138T12 22Z"/></svg>
          <span>Já confirmado{{ filteredAlreadyConfirmed.length === 1 ? '' : 's' }}</span>
        </div>
        <div class="already-list">
          <div class="already-item" v-for="item in filteredAlreadyConfirmed" :key="item.id">
            <span class="already-name">{{ item.name }}</span>
            <EquipeChip :equipe="item.equipe" />
          </div>
        </div>
      </div>
    </template>

    <TransporteModal
      :visible="!!checkinTarget"
      :participant="checkinTarget"
      :loading="confirming"
      @select="handleSelect"
      @cancel="closeModal"
    />
  </div>
</template>

<script>
import { useAcampantesStore } from '../stores/acampantes'
import TransporteModal from '../components/TransporteModal.vue'
import EquipeChip from '../components/EquipeChip.vue'
import SkeletonLoader from '../components/ui/SkeletonLoader.vue'
import EmptyState from '../components/ui/EmptyState.vue'

function normalize(s) {
  return (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

export default {
  name: 'CheckInPage',
  components: { TransporteModal, EquipeChip, SkeletonLoader, EmptyState },
  data() {
    return {
      searchQuery: '',
      checkinTarget: null,
      confirming: null
    }
  },
  computed: {
    store() { return useAcampantesStore() },
    pendingCount() { return this.store.pendingCount },
    filteredPending() {
      const q = this.searchQuery.trim() ? normalize(this.searchQuery) : null
      const items = this.store.pending
      if (!q) return items
      return items.filter(a => normalize(a.name).includes(q) || normalize(a.contato).includes(q))
    },
    // Aparece apenas quando há busca ativa, pra alertar o operador
    // que o acampante existe mas já fez check-in (não pode tocar/confirmar de novo).
    filteredAlreadyConfirmed() {
      if (!this.searchQuery.trim()) return []
      const q = normalize(this.searchQuery)
      return this.store.confirmed
        .filter(a => normalize(a.name).includes(q) || normalize(a.contato).includes(q))
    }
  },
  methods: {
    openCheckin(item) {
      this.checkinTarget = item
      this.confirming = null
    },
    closeModal() {
      if (this.confirming) return
      this.checkinTarget = null
    },
    async handleSelect(transporte) {
      if (!this.checkinTarget) return
      const target = this.checkinTarget
      this.confirming = transporte
      try {
        const res = await this.store.checkin(target.id, { transporte })
        // Sucesso real é notificado via Pusher no App.vue.
        if (res.alreadyCheckedIn) {
          this.$toast.open({
            message: `${target.name} já estava confirmado(a)`,
            type: 'warning',
            position: 'top-right',
            duration: 4000
          })
        }
        this.checkinTarget = null
      } catch (err) {
        const msg = err.response?.data?.message || 'Erro ao confirmar check-in'
        this.$toast.open({ message: msg, type: 'error', position: 'top-right', duration: 4000 })
      } finally {
        this.confirming = null
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-base);
  gap: var(--spacing-md);
}
.page-header .page-title { margin-bottom: 0; }
.pending-counter {
  display: flex;
  align-items: baseline;
  gap: var(--spacing-sm);
  padding: 6px var(--spacing-base);
  background: rgba(243, 156, 18, 0.1);
  color: var(--color-warning);
  border-radius: var(--radius-md);
  font-weight: 700;
}
.counter-num { font-size: var(--text-xl); line-height: 1; }
.counter-label { font-size: var(--text-xs); text-transform: uppercase; letter-spacing: 0.5px; }

.search-box {
  position: relative;
  max-width: 420px;
  margin-bottom: var(--spacing-base);
}
.search-input { padding-left: 38px; padding-right: 38px; }
.search-icon {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  color: var(--color-text-muted); pointer-events: none;
}
.clear-btn {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: var(--color-text-muted);
  padding: 4px; display: inline-flex;
}
.clear-btn:hover { color: var(--color-danger); }

.checkin-list { display: flex; flex-direction: column; gap: var(--spacing-md); }
.checkin-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-base);
  padding: var(--spacing-base);
}
.item-main { display: flex; flex-direction: column; gap: var(--spacing-xs); min-width: 0; flex: 1; }
.item-name-row { display: flex; align-items: center; gap: var(--spacing-sm); flex-wrap: wrap; }
.item-name { font-size: var(--text-md); font-weight: 600; color: var(--color-text-primary); }
.item-contato { font-size: var(--text-sm); color: var(--color-text-muted); }

.btn-checkin {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 10px 20px;
  background: var(--color-success);
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 700;
  cursor: pointer;
  transition: var(--transition-fast);
  box-shadow: var(--shadow-button);
  white-space: nowrap;
}
.btn-checkin:hover {
  background: #27ae60;
  box-shadow: var(--shadow-button-hover);
  transform: translateY(-1px);
}

/* Já confirmados (lista reduzida só de aviso) */
.already-section {
  margin-top: var(--spacing-lg);
  padding-top: var(--spacing-base);
  border-top: 1px dashed var(--color-bg-separator);
}
.already-header {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-success);
  margin-bottom: var(--spacing-md);
}
.already-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}
.already-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: 8px 14px;
  background: rgba(46, 204, 113, 0.06);
  border-left: 3px solid var(--color-success);
  border-radius: var(--radius-md);
  cursor: default;
}
.already-name {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-text-secondary);
  text-decoration: line-through;
  text-decoration-color: rgba(46, 204, 113, 0.4);
}

@media (max-width: 600px) {
  .checkin-item { flex-direction: column; align-items: stretch; }
  .btn-checkin { justify-content: center; }
}
</style>
